import React, { useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';

import { DataGridIdentifiers, DatagridPremiumUi } from '../Interface';
import { dataGridPremiumColumns } from '../../utils/helper';
import inventoryConversionService from '../../service/inventoryConversion.service';
import {
  getInventoryConversionBinLabel,
  getInventoryConversionCreatedByLabel,
  getInventoryConversionDocDate,
  getInventoryConversionItemLabel,
  getInventoryConversionTypeConfig,
  getInventoryConversionUnitCode,
  getInventoryConversionWarehouseLabel,
} from './inventoryConversionHelper';

const InventoryConversionReports = ({ refetchTable, setRefetchTable, rowSelectionModel, setRowSelectionModel, setSelectedValues, users }) => {
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'id', sort: 'desc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getList = (gridState, signal) => {
    inventoryConversionService.getItems(gridState, signal).then((data) => {
      if (Array.isArray(data)) {
        setReports({
          rows: data,
          rowCount: data.length,
        });
        return;
      }

      setReports(data ?? { rows: [], rowCount: 0 });
    });
  };

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'conversionType',
        headerName: 'Művelet',
        type: 'singleSelect',
        minWidth: 170,
        flex: 0.9,
        valueOptions: [
          { value: 'ASSEMBLY', label: 'Összeállítás' },
          { value: 'DISASSEMBLY', label: 'Bontás' },
        ],
        renderCell: (params) => {
          const typeConfig = getInventoryConversionTypeConfig(params.row?.conversionType);
          return (
            <Chip
              size="small"
              label={`${typeConfig.marker} ${typeConfig.label}`}
              sx={{
                bgcolor: typeConfig.backgroundColor,
                color: typeConfig.color,
                border: `1px solid ${typeConfig.borderColor}`,
                fontWeight: 700,
              }}
            />
          );
        },
      },
      {
        field: 'itemCode',
        headerName: 'Cikk',
        type: 'string',
        minWidth: 220,
        flex: 1.4,
        renderCell: (params) => {
          return (
            <Box sx={{ py: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {getInventoryConversionItemLabel(params.row) || '-'}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'qty',
        headerName: 'Mennyiség',
        type: 'number',
        minWidth: 140,
        flex: 0.8,
        renderCell: (params) => {
          return [params.row?.qty, getInventoryConversionUnitCode(params.row)].filter(Boolean).join(' ');
        },
      },
      {
        field: 'docDate',
        headerName: 'Dátum',
        type: 'dateTime',
        minWidth: 170,
        flex: 0.9,
        valueGetter: (_value, row) => {
          const rawDate = row?.docDate ?? row?.createdDate ?? row?.createDate ?? row?.createdAt ?? null;
          return rawDate ? new Date(rawDate) : null;
        },
        renderCell: (params) => getInventoryConversionDocDate(params.row),
      },
      {
        field: 'createdBy',
        headerName: 'Felhasználó',
        type: 'string',
        minWidth: 150,
        flex: 0.9,
        valueGetter: (_value, row) => getInventoryConversionCreatedByLabel(row, users),
        renderCell: (params) => getInventoryConversionCreatedByLabel(params.row, users),
      },
      {
        field: 'whsCode',
        headerName: 'Raktár',
        type: 'string',
        minWidth: 150,
        flex: 1,
        renderCell: (params) => getInventoryConversionWarehouseLabel(params.row),
      },
      {
        field: 'binId',
        headerName: 'Tárhely',
        type: 'string',
        minWidth: 130,
        flex: 0.8,
        renderCell: (params) => getInventoryConversionBinLabel(params.row),
      },
      {
        field: 'note',
        headerName: 'Megjegyzés',
        type: 'string',
        minWidth: 180,
        flex: 1.2,
      },
    ]);
  }, [users]);

  return (
    <Box sx={{ height: '100%' }}>
      <DatagridPremiumUi
        datagridIdentifier={DataGridIdentifiers.inventoryConversion}
        datagridInitialState={initialState}
        dataLoader={getList}
        rows={reports.rows}
        refetch={{ refetchTable, setRefetchTable }}
        rowCount={reports.rowCount}
        columns={columns}
        paginationAndfilterMode="server"
        onRowClick={(rowData) => {
          inventoryConversionService.getItemById(rowData.row.id).then((data) => {
            setSelectedValues(data);
            setRowSelectionModel([rowData.row.id]);
          });
        }}
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        rowSelectionModel={rowSelectionModel}
        disableRowSelectionOnClick={false}
      />
    </Box>
  );
};

export default InventoryConversionReports;
